import BusinessIcon from "@mui/icons-material/Business";
import LockIcon from "@mui/icons-material/Lock";
import PersonIcon from "@mui/icons-material/Person";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { Box, Tab, useMediaQuery, useTheme } from "@mui/material";
import React, { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import AdminData from "../components/AdminData";
import ClientData from "../components/ClientData";
import MobileAdminData from "../components/MobileAdminData";
import PageLayout from "../components/PageLayout";
import ProfileData from "../components/ProfileData";
import { useApi } from "../hooks/useApi";
import { ClientProfile } from "../types/client";
import { Profile } from "../types/contact";

interface Props { }

const Settings: FunctionComponent<Props> = (props: Props): JSX.Element => {
  const { data: profile } = useApi<Profile>("profile");
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<string>("1");
  const [isPrimaryAdmin, setIsPrimaryAdmin] = useState<boolean>(false);

  const { t } = useTranslation();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"))

  useEffect(() => {
    if (profile?.results?.clients && profile.results.clients.length > 0) {
      const clients: ClientProfile[] = profile?.results?.clients || [];
      setIsPrimaryAdmin(clients.some((client) => client.isPrimary));
    }
  }, [profile]);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (isPrimaryAdmin) {
      setTab(tab ?? "1");
    } else {
      setTab("1");
    }
  }, [isPrimaryAdmin, searchParams]);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setTab(newValue);
    setSearchParams({ tab: newValue });
  };

  return (
    <PageLayout header={t("settings.title")}>
      <TabContext value={tab}>
        {!isSmallScreen && (
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <TabList onChange={handleChange} aria-label="lab API tabs example">
              <Tab
                label={t("profilePage.title")}
                value="1"
                icon={<PersonIcon />}
                iconPosition="start"
              />

              {isPrimaryAdmin && (
                <Tab
                  label={t("clientPage.title")}
                  value="2"
                  icon={<BusinessIcon />}
                  iconPosition="start"
                  disabled={!isPrimaryAdmin}
                />
              )}

              {isPrimaryAdmin && (
                <Tab
                  label={t("adminPage.title")}
                  value="3"
                  icon={<LockIcon />}
                  iconPosition="start"
                  disabled={!isPrimaryAdmin}
                />
              )}
            </TabList>
          </Box>
        )}

        <TabPanel value="1" sx={{ padding: isSmallScreen ? 1 : 3 }}>
          <ProfileData />
        </TabPanel>

        <TabPanel value="2" sx={{ padding: isSmallScreen ? 1 : 3 }}>
          <ClientData />
        </TabPanel>

        <TabPanel value="3" sx={{ padding: isSmallScreen ? 1 : 3 }}>
          {isSmallScreen || isMediumScreen ? <MobileAdminData /> : <AdminData />}
        </TabPanel>
      </TabContext>
    </PageLayout>
  );
};

export default Settings;
